import type { FloorplanRoom } from "../types";
import type { FurnitureItem } from "./types";
import { FurnitureRenderer } from "./FurnitureRenderer";
import { getBackendKindForType, getCatalogEntry } from "./catalog";
import { getRoomColor } from "../FloorplanRoomOverlay";

interface FurnitureOverlayProps {
  rooms: FloorplanRoom[];
  scale: number;
  /**
   * Legacy single toggle. When `kindsVisible` is provided it takes
   * precedence; otherwise `showAll` is treated as "all three kinds visible".
   */
  showAll?: boolean;
  /**
   * Per-backend-model visibility. Each flag gates items whose catalog kind
   * resolves to that backend model. Items with `alwaysVisible: true` (fire
   * extinguisher, AED, etc.) ignore these flags and always render.
   */
  kindsVisible?: { furniture: boolean; equipment: boolean; desk: boolean };
  /** Floor-level furniture not tied to any room (common areas). */
  floorFurniture?: FurnitureItem[];
  /**
   * Fired when a furniture item with `representsRoomId` is clicked. The
   * `via` argument is the item that triggered the click — viewers can use
   * it to show a "(via [item label])" hint in the room detail panel.
   * Falls back to the polygon-click `onRoomClick` shape (room only) so it
   * can be wired straight through to existing room-click handlers.
   */
  onRoomClick?: (room: FloorplanRoom, via?: FurnitureItem) => void;
  /** Fired when any furniture/desk/equipment item is clicked (no representsRoomId). */
  onItemClick?: (item: FurnitureItem) => void;
  /** Used by getRoomColor to pick the right palette (mgmt vs tenant view). */
  showManagementDetails?: boolean;
  /** The Occupancy layer: off removes the in-use stripes and the free tint. Default on. */
  showOccupancy?: boolean;
}

export function FurnitureOverlay({ rooms, scale, showAll = true, kindsVisible, floorFurniture, onRoomClick, onItemClick, showManagementDetails, showOccupancy = true }: FurnitureOverlayProps) {
  const visibility = kindsVisible ?? { furniture: showAll, equipment: showAll, desk: showAll };
  const shouldRender = (item: FurnitureItem) => {
    const entry = getCatalogEntry(item.type);
    // alwaysVisible (fire extinguisher, AED, booths) bypasses layer toggles.
    if (entry?.alwaysVisible || item.alwaysVisible) return true;
    return visibility[getBackendKindForType(item.type)];
  };

  // Build a lookup so click handlers can resolve representsRoomId → FloorplanRoom in O(1).
  const roomById = new Map<number, FloorplanRoom>();
  for (const r of rooms) roomById.set(r.id, r);

  const renderItem = (item: FurnitureItem, roomTenantId?: number | null, roomColor?: string) => {
    if (!shouldRender(item)) return null;
    const linkedRoom = item.representsRoomId != null ? roomById.get(item.representsRoomId) : undefined;
    if (linkedRoom && onRoomClick) {
      return (
        <g
          key={item.id}
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onRoomClick(linkedRoom, item);
          }}
        >
          <FurnitureRenderer item={item} scale={scale} roomTenantId={roomTenantId} roomColor={roomColor} showOccupancy={showOccupancy} />
        </g>
      );
    }
    if (onItemClick) {
      return (
        <g
          key={item.id}
          style={{ cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            onItemClick(item);
          }}
        >
          <FurnitureRenderer item={item} scale={scale} roomTenantId={roomTenantId} roomColor={roomColor} showOccupancy={showOccupancy} />
        </g>
      );
    }
    return <FurnitureRenderer key={item.id} item={item} scale={scale} roomTenantId={roomTenantId} roomColor={roomColor} showOccupancy={showOccupancy} />;
  };

  const activeTenantIdFor = (room: FloorplanRoom): number | null | undefined => {
    const occ = (room.occupancies ?? []).find((o) => !o.endDate || o.endDate >= new Date().toISOString().slice(0, 10));
    return occ?.tenantId ?? null;
  };

  return (
    <g>
      {rooms.map((room) => {
        if (!room.polygon?.length || !room.polygon[0]?.length) return null;
        const roomTenantId = activeTenantIdFor(room);
        const roomColor = getRoomColor(room, !!showManagementDetails);
        return (room.furniture ?? []).map((item) => renderItem(item, roomTenantId, roomColor));
      })}
      {(floorFurniture ?? []).map((item) => renderItem(item, null, undefined))}
    </g>
  );
}
